import React, { useState, useEffect, useMemo } from 'react';
import { TextField, Popper, ClickAwayListener } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { ResponseConfigOption } from '../../types/common';
import { textFieldSX } from '../../constants/textFieldCSS';
import { WorkflowServiceIcon } from './WorkflowServiceIcon';

interface DropdownPopperProps {
    open: boolean;
    anchorEl: HTMLElement | null;
    options: ResponseConfigOption[];
    onClose: () => void;
    onOptionClick: (option: ResponseConfigOption) => void;
    headerText?: string;
    placement?: 'left-start' | 'bottom-start';
    width?: number | string;
    countLabel?: (count: string) => string;
    groupByKey?: 'count' | 'refNodeId';
}

const DropdownPopper: React.FC<DropdownPopperProps> = ({
    open,
    anchorEl,
    options,
    onClose,
    onOptionClick,
    headerText,
    placement = 'left-start',
    width = 500,
    countLabel = (count) => count === 'Other' ? 'Other' : `${count}`,
    groupByKey = 'count'
}) => {
    const [searchText, setSearchText] = useState('');
    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

    // Group options
    const groupedOptions = useMemo(() => {
        const groups: Record<string, ResponseConfigOption[]> = {};
        options?.forEach((option) => {
            const key = groupByKey === 'count'
                ? (option.count?.toString() || 'Other')
                : `${option.count || 'Other'}-${option.refNodeId}`;

            if (!groups[key]) {
                groups[key] = [];
            }
            groups[key].push(option);
        });
        return groups;
    }, [options, groupByKey]);

    // Initialize expanded state
    useEffect(() => {
        const initialState: Record<string, boolean> = {};
        Object.keys(groupedOptions).forEach((key) => {
            initialState[key] = true;
        });
        setExpandedGroups(initialState);
    }, [groupedOptions]);

    // Filter options based on search text
    const filteredOptions = useMemo(() => {
        const filtered: Record<string, ResponseConfigOption[]> = {};
        Object.entries(groupedOptions).forEach(([key, groupOptions]) => {
            const matchingOptions = groupOptions.filter(option =>
                String(option.id || '').toLowerCase().includes(searchText.toLowerCase()) ||
                String(option.value || '').toLowerCase().includes(searchText.toLowerCase())
            );

            if (matchingOptions.length > 0) {
                filtered[key] = matchingOptions;
            }
        });
        return filtered;
    }, [groupedOptions, searchText]);

    const handleClose = () => {
        onClose();
        setSearchText('');
    };

    const isEmpty = (groupOptions: ResponseConfigOption[]) => groupOptions.length > 0 && groupOptions[0].id === "";
    return (
        <Popper
            open={open}
            anchorEl={anchorEl}
            placement={placement}
            style={{ width, zIndex: 1300 }}
        >
            <ClickAwayListener onClickAway={handleClose}>
                <div className="bg-white rounded shadow-lg border border-borderGrey p-4">
                    {headerText && (
                        <div className="font-medium mb-4">{headerText}</div>
                    )}
                    <>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Search..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            sx={textFieldSX}
                            className="mb-4"
                        />
                        <div className="max-h-[300px] overflow-y-auto">
                            {Object.entries(filteredOptions).map(([groupKey, groupOptions]) => (
                                <div key={groupKey} className="border-b border-borderGrey last:border-b-0">
                                    <div
                                        className="flex items-center justify-between p-2 bg-backgroundGrey cursor-pointer hover:bg-gray-100"
                                        onClick={() => setExpandedGroups(prev => ({
                                            ...prev,
                                            [groupKey]: !prev[groupKey]
                                        }))}
                                    >
                                        <span className="font-medium">
                                            {countLabel(groupKey.split('-')[0])}. {groupOptions[0].nodeName}
                                        </span>
                                        {expandedGroups[groupKey] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </div>

                                    {isEmpty(groupOptions) && expandedGroups[groupKey] ? (
                                        <div className="p-4">
                                            <h2 className="text-xl font-bold mb-4">First, you need to complete this step</h2>
                                            <p className="text-gray-700 mb-6">
                                                Configuring the step gives you access to {groupOptions[0]?.valueDetail}'s data to use in fields like this one. Go complete this step & come back to have access to its data.
                                            </p>
                                            {/* <button 
                                                className="bg-[#7C3AED] text-white px-6 py-3 rounded-lg font-medium"
                                                onClick={handleClose}
                                            >
                                                Complete this step
                                            </button> */}
                                        </div>
                                    ) : (
                                        expandedGroups[groupKey] && groupOptions.map((option) => (
                                            <div
                                                key={`${option.id}:${option.refNodeId}`}
                                                className="flex items-center gap-2 p-2 hover:bg-backgroundGrey cursor-pointer ml-4"
                                                onClick={() => {
                                                    onOptionClick(option);
                                                    handleClose();
                                                }}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <WorkflowServiceIcon
                                                        workflowType={option.valueDetail || ''}
                                                        alt={option.valueDetail}
                                                        height={20}
                                                        width={20}
                                                    />
                                                    <div className="text-greyishBlue">{option.count ? `${option.count} |` : ''}</div>
                                                    <div className="font-medium">{option.id}</div>
                                                </div>
                                                <div className="text-greyishBlueLight text-sm truncate">
                                                    {typeof option.value !== "object" ? option.value : ""}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                </div>
            </ClickAwayListener>
        </Popper>
    );
};

export default DropdownPopper; 